import {useCallback, useMemo} from 'react';
import {v4 as uuidv4} from 'uuid';
import {
    RequirementLevel,
    ResourceAllocationState,
    Tag,
    TagScope
} from '../../../types';

type AuthenticatedFetch = (
    url: string,
    options?: RequestInit
) => Promise<Response>;

interface SignalRConnection {
    getConnectionId(): string | null;
}

interface ActionDeps {
    // Ref, not the state value: callbacks read state via stateRef.current so
    // their identities survive state changes — the provider value can then be
    // memoized and downstream memo boundaries hold.
    stateRef: {current: ResourceAllocationState};
    setState: React.Dispatch<React.SetStateAction<ResourceAllocationState>>;
    authenticatedFetch: AuthenticatedFetch;
    signalRService: SignalRConnection;
    customerId: string | undefined;
    baseUrl: string;
}

export const useTagActions = ({
    stateRef,
    setState,
    authenticatedFetch,
    signalRService,
    customerId,
    baseUrl
}: ActionDeps) => {
    // Tag CRUD operations
    const addTag = useCallback(
        async (tag: Omit<Tag, 'id' | 'createdAt'>): Promise<string> => {
            const id = uuidv4();
            const createdAt = new Date().toISOString();
            const newTag = {...tag, id, createdAt};

            // Optimistic update
            setState(prev => ({
                ...prev,
                tags: {...prev.tags, [id]: newTag}
            }));

            // API call — awaited so callers can sequence dependent creates and
            // surface failures (the server persists this same client-generated id).
            if (customerId && authenticatedFetch) {
                const connectionId = signalRService.getConnectionId();

                const revert = () =>
                    setState(prev => {
                        const {[id]: _removed, ...rest} = prev.tags;
                        return {...prev, tags: rest};
                    });

                try {
                    const response = await authenticatedFetch(
                        `${baseUrl}/api/resource-allocation/tags`,
                        {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                'X-SignalR-Connection-Id': connectionId || ''
                            },
                            body: JSON.stringify({
                                id,
                                name: tag.name,
                                colour: tag.colour,
                                icon: tag.icon,
                                isActive: tag.isActive,
                                parentTagId: tag.parentTagId ?? null,
                                appliesTo: tag.appliesTo ?? TagScope.Any
                            })
                        }
                    );

                    if (!response.ok) {
                        const errorText = await response.text();
                        console.error('[addTag] API call failed:', errorText);
                        revert();
                        throw new Error(`Failed to create tag: ${errorText}`);
                    }
                } catch (error: unknown) {
                    console.error('[addTag] API call error:', error);
                    revert();
                    throw error;
                }
            }

            return id;
        },
        [
            setState,
            authenticatedFetch,
            signalRService,
            customerId,
            baseUrl
        ]
    );

    const updateTag = useCallback(
        (id: string, updates: Partial<Tag>) => {
            const state = stateRef.current;
            const previousTag = state.tags[id];

            // Optimistic update
            setState(prev => ({
                ...prev,
                tags: {
                    ...prev.tags,
                    [id]: {...prev.tags[id], ...updates}
                }
            }));

            // API call
            if (customerId && authenticatedFetch) {
                const connectionId = signalRService.getConnectionId();

                authenticatedFetch(
                    `${baseUrl}/api/resource-allocation/tags/${id}`,
                    {
                        method: 'PUT',
                        headers: {
                            'Content-Type': 'application/json',
                            'X-SignalR-Connection-Id': connectionId || ''
                        },
                        body: JSON.stringify(updates)
                    }
                )
                    .then(async (response: Response) => {
                        if (!response.ok) {
                            console.error(
                                '[updateTag] API call failed:',
                                await response.text()
                            );
                            // Revert
                            setState(prev => ({
                                ...prev,
                                tags: {...prev.tags, [id]: previousTag}
                            }));
                        }
                    })
                    .catch((error: unknown) => {
                        console.error('[updateTag] API call error:', error);
                        setState(prev => ({
                            ...prev,
                            tags: {...prev.tags, [id]: previousTag}
                        }));
                    });
            }
        },
        [
            stateRef,
            setState,
            authenticatedFetch,
            signalRService,
            customerId,
            baseUrl
        ]
    );

    // Expire a tag (the default "delete" action): retired but kept for history so
    // any project still tagged with it continues to resolve. The tag and its
    // project-tag associations are preserved; we just patch the record.
    const expireTag = useCallback(
        (id: string) => {
            const state = stateRef.current;
            const previousTag = state.tags[id];
            if (!previousTag) return;
            const expiredOn = new Date().toISOString();

            setState(prev => ({
                ...prev,
                tags: {
                    ...prev.tags,
                    [id]: {...prev.tags[id], expiredOn, isActive: false}
                }
            }));

            const revert = () =>
                setState(prev => ({
                    ...prev,
                    tags: {...prev.tags, [id]: previousTag}
                }));

            if (customerId && authenticatedFetch) {
                const connectionId = signalRService.getConnectionId();
                authenticatedFetch(
                    `${baseUrl}/api/resource-allocation/tags/${id}`,
                    {
                        method: 'DELETE',
                        headers: {'X-SignalR-Connection-Id': connectionId || ''}
                    }
                )
                    .then(async (response: Response) => {
                        if (!response.ok) {
                            console.error(
                                '[expireTag] API call failed:',
                                await response.text()
                            );
                            revert();
                        }
                    })
                    .catch((error: unknown) => {
                        console.error('[expireTag] API call error:', error);
                        revert();
                    });
            }
        },
        [
            stateRef,
            setState,
            authenticatedFetch,
            signalRService,
            customerId,
            baseUrl
        ]
    );

    // Un-expire a tag — clears expiredOn so it returns to active selection.
    const reinstateTag = useCallback(
        (id: string) => {
            const state = stateRef.current;
            const previousTag = state.tags[id];
            if (!previousTag) return;

            setState(prev => ({
                ...prev,
                tags: {
                    ...prev.tags,
                    [id]: {...prev.tags[id], expiredOn: null, isActive: true}
                }
            }));

            const revert = () =>
                setState(prev => ({
                    ...prev,
                    tags: {...prev.tags, [id]: previousTag}
                }));

            if (customerId && authenticatedFetch) {
                const connectionId = signalRService.getConnectionId();
                authenticatedFetch(
                    `${baseUrl}/api/resource-allocation/tags/${id}/reinstate`,
                    {
                        method: 'POST',
                        headers: {'X-SignalR-Connection-Id': connectionId || ''}
                    }
                )
                    .then(async (response: Response) => {
                        if (!response.ok) {
                            console.error(
                                '[reinstateTag] API call failed:',
                                await response.text()
                            );
                            revert();
                        }
                    })
                    .catch((error: unknown) => {
                        console.error('[reinstateTag] API call error:', error);
                        revert();
                    });
            }
        },
        [
            stateRef,
            setState,
            authenticatedFetch,
            signalRService,
            customerId,
            baseUrl
        ]
    );

    // Permanently delete a tag (created-in-error escape hatch). Rejects if the
    // server refuses (still applied to projects) so the caller can surface why.
    const permanentlyDeleteTag = useCallback(
        async (id: string): Promise<void> => {
            const state = stateRef.current;
            const previousTag = state.tags[id];
            const previousProjectTags = Object.fromEntries(
                Object.entries(state.projectTags).filter(
                    ([, pt]) => pt.tagId === id
                )
            );
            const previousPersonTags = Object.fromEntries(
                Object.entries(state.personTags).filter(
                    ([, pt]) => pt.tagId === id
                )
            );
            const previousAssignmentTags = Object.fromEntries(
                Object.entries(state.assignmentTags).filter(
                    ([, at]) => at.tagId === id
                )
            );
            const previousTeamTags = Object.fromEntries(
                Object.entries(state.teamTags).filter(
                    ([, tt]) => tt.tagId === id
                )
            );

            setState(prev => {
                const {[id]: _removed, ...restTags} = prev.tags;
                const newProjectTags = Object.fromEntries(
                    Object.entries(prev.projectTags).filter(
                        ([, pt]) => pt.tagId !== id
                    )
                );
                const newPersonTags = Object.fromEntries(
                    Object.entries(prev.personTags).filter(
                        ([, pt]) => pt.tagId !== id
                    )
                );
                const newAssignmentTags = Object.fromEntries(
                    Object.entries(prev.assignmentTags).filter(
                        ([, at]) => at.tagId !== id
                    )
                );
                const newTeamTags = Object.fromEntries(
                    Object.entries(prev.teamTags).filter(
                        ([, tt]) => tt.tagId !== id
                    )
                );
                return {
                    ...prev,
                    tags: restTags,
                    projectTags: newProjectTags,
                    personTags: newPersonTags,
                    assignmentTags: newAssignmentTags,
                    teamTags: newTeamTags
                };
            });

            const revert = () =>
                setState(prev => ({
                    ...prev,
                    tags: {...prev.tags, [id]: previousTag},
                    projectTags: {...prev.projectTags, ...previousProjectTags},
                    personTags: {...prev.personTags, ...previousPersonTags},
                    assignmentTags: {
                        ...prev.assignmentTags,
                        ...previousAssignmentTags
                    },
                    teamTags: {...prev.teamTags, ...previousTeamTags}
                }));

            if (!customerId || !authenticatedFetch) return;

            const connectionId = signalRService.getConnectionId();
            let response: Response;
            try {
                response = await authenticatedFetch(
                    `${baseUrl}/api/resource-allocation/tags/${id}/permanent`,
                    {
                        method: 'DELETE',
                        headers: {'X-SignalR-Connection-Id': connectionId || ''}
                    }
                );
            } catch (error: unknown) {
                console.error('[permanentlyDeleteTag] API call error:', error);
                revert();
                throw error;
            }

            if (!response.ok) {
                const message = await response.text();
                console.error(
                    '[permanentlyDeleteTag] API call failed:',
                    message
                );
                revert();
                throw new Error(message || 'Failed to permanently delete tag');
            }
        },
        [
            stateRef,
            setState,
            authenticatedFetch,
            signalRService,
            customerId,
            baseUrl
        ]
    );

    // Project-Tag association operations
    const addProjectTag = useCallback(
        (projectId: string, tagId: string): string => {
            const state = stateRef.current;
            // Check if association already exists
            const existingAssociation = Object.values(state.projectTags).find(
                pt => pt.projectId === projectId && pt.tagId === tagId
            );

            if (existingAssociation) {
                return existingAssociation.id;
            }

            const id = uuidv4();
            const newProjectTag = {id, projectId, tagId};

            // Optimistic update
            setState(prev => ({
                ...prev,
                projectTags: {
                    ...prev.projectTags,
                    [id]: newProjectTag
                }
            }));

            // API call
            if (customerId && authenticatedFetch) {
                const connectionId = signalRService.getConnectionId();

                authenticatedFetch(
                    `${baseUrl}/api/resource-allocation/project-tags`,
                    {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'X-SignalR-Connection-Id': connectionId || ''
                        },
                        body: JSON.stringify({
                            id,
                            projectId,
                            tagId
                        })
                    }
                )
                    .then(async (response: Response) => {
                        if (!response.ok) {
                            console.error(
                                '[addProjectTag] API call failed:',
                                await response.text()
                            );
                            // Revert
                            setState(prev => {
                                const {[id]: _removed, ...rest} =
                                    prev.projectTags;
                                return {...prev, projectTags: rest};
                            });
                        }
                    })
                    .catch((error: unknown) => {
                        console.error('[addProjectTag] API call error:', error);
                        setState(prev => {
                            const {[id]: _removed, ...rest} = prev.projectTags;
                            return {...prev, projectTags: rest};
                        });
                    });
            }

            return id;
        },
        [
            stateRef,
            setState,
            authenticatedFetch,
            signalRService,
            customerId,
            baseUrl
        ]
    );

    const removeProjectTag = useCallback(
        (projectTagId: string) => {
            const previousProjectTag =
                stateRef.current.projectTags[projectTagId];

            setState(prev => {
                const {[projectTagId]: _removed, ...rest} = prev.projectTags;
                return {...prev, projectTags: rest};
            });

            // API call
            if (customerId && authenticatedFetch) {
                const connectionId = signalRService.getConnectionId();

                authenticatedFetch(
                    `${baseUrl}/api/resource-allocation/project-tags/${projectTagId}`,
                    {
                        method: 'DELETE',
                        headers: {
                            'X-SignalR-Connection-Id': connectionId || ''
                        }
                    }
                )
                    .then(async (response: Response) => {
                        if (!response.ok) {
                            console.error(
                                '[removeProjectTag] API call failed:',
                                await response.text()
                            );
                            // Revert
                            setState(prev => ({
                                ...prev,
                                projectTags: {
                                    ...prev.projectTags,
                                    [projectTagId]: previousProjectTag
                                }
                            }));
                        }
                    })
                    .catch((error: unknown) => {
                        console.error(
                            '[removeProjectTag] API call error:',
                            error
                        );
                        setState(prev => ({
                            ...prev,
                            projectTags: {
                                ...prev.projectTags,
                                [projectTagId]: previousProjectTag
                            }
                        }));
                    });
            }
        },
        [
            stateRef,
            setState,
            authenticatedFetch,
            signalRService,
            customerId,
            baseUrl
        ]
    );

    const removeProjectTagByIds = useCallback(
        (projectId: string, tagId: string) => {
            setState(prev => {
                const newProjectTags = Object.fromEntries(
                    Object.entries(prev.projectTags).filter(
                        ([, pt]) =>
                            !(pt.projectId === projectId && pt.tagId === tagId)
                    )
                );
                return {...prev, projectTags: newProjectTags};
            });
        },
        [setState]
    );

    const getProjectTags = useCallback(
        (projectId: string): Tag[] => {
            const state = stateRef.current;
            const projectTagAssociations = Object.values(
                state.projectTags
            ).filter(pt => pt.projectId === projectId);

            return projectTagAssociations
                .map(pt => state.tags[pt.tagId])
                .filter((tag): tag is Tag => tag !== undefined);
        },
        [stateRef]
    );

    const getTagProjects = useCallback(
        (tagId: string): string[] => {
            return Object.values(stateRef.current.projectTags)
                .filter(pt => pt.tagId === tagId)
                .map(pt => pt.projectId);
        },
        [stateRef]
    );

    // ==================== Person-Tag associations ====================
    const addPersonTag = useCallback(
        (personId: string, tagId: string): string => {
            const existing = Object.values(stateRef.current.personTags).find(
                pt => pt.personId === personId && pt.tagId === tagId
            );
            if (existing) return existing.id;

            const id = uuidv4();
            const record = {id, personId, tagId};

            setState(prev => ({
                ...prev,
                personTags: {...prev.personTags, [id]: record}
            }));

            if (customerId && authenticatedFetch) {
                const connectionId = signalRService.getConnectionId();
                const revert = () =>
                    setState(prev => {
                        const {[id]: _removed, ...rest} = prev.personTags;
                        return {...prev, personTags: rest};
                    });
                authenticatedFetch(
                    `${baseUrl}/api/resource-allocation/person-tags`,
                    {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'X-SignalR-Connection-Id': connectionId || ''
                        },
                        body: JSON.stringify({id, personId, tagId})
                    }
                )
                    .then(async (response: Response) => {
                        if (!response.ok) {
                            console.error(
                                '[addPersonTag] API call failed:',
                                await response.text()
                            );
                            revert();
                        }
                    })
                    .catch((error: unknown) => {
                        console.error('[addPersonTag] API call error:', error);
                        revert();
                    });
            }

            return id;
        },
        [stateRef, setState, authenticatedFetch, signalRService, customerId, baseUrl]
    );

    // Remove a person's tag by person + tag (API-backed): find the association and
    // delete it on the server so a deselection actually persists.
    const removePersonTagByIds = useCallback(
        (personId: string, tagId: string) => {
            const association = Object.values(
                stateRef.current.personTags
            ).find(
                pt => pt.personId === personId && pt.tagId === tagId
            );
            if (!association) return;
            const joinId = association.id;

            setState(prev => {
                const {[joinId]: _removed, ...rest} = prev.personTags;
                return {...prev, personTags: rest};
            });

            if (customerId && authenticatedFetch) {
                const connectionId = signalRService.getConnectionId();
                authenticatedFetch(
                    `${baseUrl}/api/resource-allocation/person-tags/${joinId}`,
                    {
                        method: 'DELETE',
                        headers: {'X-SignalR-Connection-Id': connectionId || ''}
                    }
                )
                    .then(async (response: Response) => {
                        if (!response.ok) {
                            console.error(
                                '[removePersonTag] API call failed:',
                                await response.text()
                            );
                            setState(prev => ({
                                ...prev,
                                personTags: {
                                    ...prev.personTags,
                                    [joinId]: association
                                }
                            }));
                        }
                    })
                    .catch((error: unknown) => {
                        console.error('[removePersonTag] API call error:', error);
                        setState(prev => ({
                            ...prev,
                            personTags: {
                                ...prev.personTags,
                                [joinId]: association
                            }
                        }));
                    });
            }
        },
        [stateRef, setState, authenticatedFetch, signalRService, customerId, baseUrl]
    );

    const getPersonTags = useCallback(
        (personId: string): Tag[] => {
            const state = stateRef.current;
            return Object.values(state.personTags)
                .filter(pt => pt.personId === personId)
                .map(pt => state.tags[pt.tagId])
                .filter((tag): tag is Tag => tag !== undefined);
        },
        [stateRef]
    );

    const getTagPeople = useCallback(
        (tagId: string): string[] => {
            return Object.values(stateRef.current.personTags)
                .filter(pt => pt.tagId === tagId)
                .map(pt => pt.personId);
        },
        [stateRef]
    );

    // ==================== Assignment-Tag associations ====================
    const addAssignmentTag = useCallback(
        (
            assignmentId: string,
            tagId: string,
            level: RequirementLevel = RequirementLevel.Must
        ): string => {
            const existing = Object.values(
                stateRef.current.assignmentTags
            ).find(
                at => at.assignmentId === assignmentId && at.tagId === tagId
            );
            if (existing) return existing.id;

            const id = uuidv4();
            const record = {id, assignmentId, tagId, level};

            setState(prev => ({
                ...prev,
                assignmentTags: {...prev.assignmentTags, [id]: record}
            }));

            if (customerId && authenticatedFetch) {
                const connectionId = signalRService.getConnectionId();
                const revert = () =>
                    setState(prev => {
                        const {[id]: _removed, ...rest} = prev.assignmentTags;
                        return {...prev, assignmentTags: rest};
                    });
                authenticatedFetch(
                    `${baseUrl}/api/resource-allocation/assignment-tags`,
                    {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'X-SignalR-Connection-Id': connectionId || ''
                        },
                        body: JSON.stringify({id, assignmentId, tagId, level})
                    }
                )
                    .then(async (response: Response) => {
                        if (!response.ok) {
                            console.error(
                                '[addAssignmentTag] API call failed:',
                                await response.text()
                            );
                            revert();
                        }
                    })
                    .catch((error: unknown) => {
                        console.error(
                            '[addAssignmentTag] API call error:',
                            error
                        );
                        revert();
                    });
            }

            return id;
        },
        [stateRef, setState, authenticatedFetch, signalRService, customerId, baseUrl]
    );

    const removeAssignmentTagByIds = useCallback(
        (assignmentId: string, tagId: string) => {
            const association = Object.values(
                stateRef.current.assignmentTags
            ).find(
                at => at.assignmentId === assignmentId && at.tagId === tagId
            );
            if (!association) return;
            const joinId = association.id;

            setState(prev => {
                const {[joinId]: _removed, ...rest} = prev.assignmentTags;
                return {...prev, assignmentTags: rest};
            });

            if (customerId && authenticatedFetch) {
                const connectionId = signalRService.getConnectionId();
                authenticatedFetch(
                    `${baseUrl}/api/resource-allocation/assignment-tags/${joinId}`,
                    {
                        method: 'DELETE',
                        headers: {'X-SignalR-Connection-Id': connectionId || ''}
                    }
                )
                    .then(async (response: Response) => {
                        if (!response.ok) {
                            console.error(
                                '[removeAssignmentTag] API call failed:',
                                await response.text()
                            );
                            setState(prev => ({
                                ...prev,
                                assignmentTags: {
                                    ...prev.assignmentTags,
                                    [joinId]: association
                                }
                            }));
                        }
                    })
                    .catch((error: unknown) => {
                        console.error(
                            '[removeAssignmentTag] API call error:',
                            error
                        );
                        setState(prev => ({
                            ...prev,
                            assignmentTags: {
                                ...prev.assignmentTags,
                                [joinId]: association
                            }
                        }));
                    });
            }
        },
        [stateRef, setState, authenticatedFetch, signalRService, customerId, baseUrl]
    );

    // Changes a requirement's level (Must/Should/Could). Optimistic with revert;
    // other clients converge via the AssignmentTagUpdated broadcast.
    const setAssignmentTagLevel = useCallback(
        (assignmentId: string, tagId: string, level: RequirementLevel) => {
            const association = Object.values(
                stateRef.current.assignmentTags
            ).find(
                at => at.assignmentId === assignmentId && at.tagId === tagId
            );
            if (!association || association.level === level) return;

            const updated = {...association, level};
            setState(prev => ({
                ...prev,
                assignmentTags: {
                    ...prev.assignmentTags,
                    [association.id]: updated
                }
            }));

            if (customerId && authenticatedFetch) {
                const connectionId = signalRService.getConnectionId();
                const revert = () =>
                    setState(prev => ({
                        ...prev,
                        assignmentTags: {
                            ...prev.assignmentTags,
                            [association.id]: association
                        }
                    }));
                authenticatedFetch(
                    `${baseUrl}/api/resource-allocation/assignment-tags/${association.id}`,
                    {
                        method: 'PUT',
                        headers: {
                            'Content-Type': 'application/json',
                            'X-SignalR-Connection-Id': connectionId || ''
                        },
                        body: JSON.stringify({level})
                    }
                )
                    .then(async (response: Response) => {
                        if (!response.ok) {
                            console.error(
                                '[setAssignmentTagLevel] API call failed:',
                                await response.text()
                            );
                            revert();
                        }
                    })
                    .catch((error: unknown) => {
                        console.error(
                            '[setAssignmentTagLevel] API call error:',
                            error
                        );
                        revert();
                    });
            }
        },
        [stateRef, setState, authenticatedFetch, signalRService, customerId, baseUrl]
    );

    const getAssignmentTags = useCallback(
        (assignmentId: string): Tag[] => {
            const state = stateRef.current;
            return Object.values(state.assignmentTags)
                .filter(at => at.assignmentId === assignmentId)
                .map(at => state.tags[at.tagId])
                .filter((tag): tag is Tag => tag !== undefined);
        },
        [stateRef]
    );

    // Same as getAssignmentTags but keeps each requirement's level — the shape
    // role cards, level editors and the candidate picker match against.
    const getAssignmentRequirements = useCallback(
        (assignmentId: string): {tag: Tag; level: RequirementLevel}[] => {
            const state = stateRef.current;
            return Object.values(state.assignmentTags)
                .filter(at => at.assignmentId === assignmentId)
                .map(at => ({
                    tag: state.tags[at.tagId],
                    level: at.level ?? RequirementLevel.Must
                }))
                .filter(
                    (entry): entry is {tag: Tag; level: RequirementLevel} =>
                        entry.tag !== undefined
                );
        },
        [stateRef]
    );

    const getTagAssignments = useCallback(
        (tagId: string): string[] => {
            return Object.values(stateRef.current.assignmentTags)
                .filter(at => at.tagId === tagId)
                .map(at => at.assignmentId);
        },
        [stateRef]
    );

    // ==================== Team-Tag associations ====================
    const addTeamTag = useCallback(
        (teamId: string, tagId: string): string => {
            const existing = Object.values(stateRef.current.teamTags).find(
                tt => tt.teamId === teamId && tt.tagId === tagId
            );
            if (existing) return existing.id;

            const id = uuidv4();
            const record = {id, teamId, tagId};

            setState(prev => ({
                ...prev,
                teamTags: {...prev.teamTags, [id]: record}
            }));

            if (customerId && authenticatedFetch) {
                const connectionId = signalRService.getConnectionId();
                const revert = () =>
                    setState(prev => {
                        const {[id]: _removed, ...rest} = prev.teamTags;
                        return {...prev, teamTags: rest};
                    });
                authenticatedFetch(
                    `${baseUrl}/api/resource-allocation/team-tags`,
                    {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'X-SignalR-Connection-Id': connectionId || ''
                        },
                        body: JSON.stringify({id, teamId, tagId})
                    }
                )
                    .then(async (response: Response) => {
                        if (!response.ok) {
                            console.error(
                                '[addTeamTag] API call failed:',
                                await response.text()
                            );
                            revert();
                        }
                    })
                    .catch((error: unknown) => {
                        console.error('[addTeamTag] API call error:', error);
                        revert();
                    });
            }

            return id;
        },
        [stateRef, setState, authenticatedFetch, signalRService, customerId, baseUrl]
    );

    const removeTeamTagByIds = useCallback(
        (teamId: string, tagId: string) => {
            const association = Object.values(stateRef.current.teamTags).find(
                tt => tt.teamId === teamId && tt.tagId === tagId
            );
            if (!association) return;
            const joinId = association.id;

            setState(prev => {
                const {[joinId]: _removed, ...rest} = prev.teamTags;
                return {...prev, teamTags: rest};
            });

            if (customerId && authenticatedFetch) {
                const connectionId = signalRService.getConnectionId();
                authenticatedFetch(
                    `${baseUrl}/api/resource-allocation/team-tags/${joinId}`,
                    {
                        method: 'DELETE',
                        headers: {'X-SignalR-Connection-Id': connectionId || ''}
                    }
                )
                    .then(async (response: Response) => {
                        if (!response.ok) {
                            console.error(
                                '[removeTeamTag] API call failed:',
                                await response.text()
                            );
                            setState(prev => ({
                                ...prev,
                                teamTags: {
                                    ...prev.teamTags,
                                    [joinId]: association
                                }
                            }));
                        }
                    })
                    .catch((error: unknown) => {
                        console.error('[removeTeamTag] API call error:', error);
                        setState(prev => ({
                            ...prev,
                            teamTags: {
                                ...prev.teamTags,
                                [joinId]: association
                            }
                        }));
                    });
            }
        },
        [stateRef, setState, authenticatedFetch, signalRService, customerId, baseUrl]
    );

    const getTeamTags = useCallback(
        (teamId: string): Tag[] => {
            const state = stateRef.current;
            return Object.values(state.teamTags)
                .filter(tt => tt.teamId === teamId)
                .map(tt => state.tags[tt.tagId])
                .filter((tag): tag is Tag => tag !== undefined);
        },
        [stateRef]
    );

    const getTagTeams = useCallback(
        (tagId: string): string[] => {
            return Object.values(stateRef.current.teamTags)
                .filter(tt => tt.tagId === tagId)
                .map(tt => tt.teamId);
        },
        [stateRef]
    );

    return useMemo(
        () => ({
            addTag,
            updateTag,
            expireTag,
            reinstateTag,
            permanentlyDeleteTag,
            addProjectTag,
            removeProjectTag,
            removeProjectTagByIds,
            getProjectTags,
            getTagProjects,
            addPersonTag,
            removePersonTagByIds,
            getPersonTags,
            getTagPeople,
            addAssignmentTag,
            setAssignmentTagLevel,
            removeAssignmentTagByIds,
            getAssignmentTags,
            getAssignmentRequirements,
            getTagAssignments,
            addTeamTag,
            removeTeamTagByIds,
            getTeamTags,
            getTagTeams
        }),
        [
            addTag,
            updateTag,
            expireTag,
            reinstateTag,
            permanentlyDeleteTag,
            addProjectTag,
            removeProjectTag,
            removeProjectTagByIds,
            getProjectTags,
            getTagProjects,
            addPersonTag,
            removePersonTagByIds,
            getPersonTags,
            getTagPeople,
            addAssignmentTag,
            setAssignmentTagLevel,
            removeAssignmentTagByIds,
            getAssignmentTags,
            getAssignmentRequirements,
            getTagAssignments,
            addTeamTag,
            removeTeamTagByIds,
            getTeamTags,
            getTagTeams
        ]
    );
};
